/**
 * @file INCSUBWalleteDetails.js
 * @author vishal lohbande
 * @description
 * This React component displays the wallet details and transaction history of a user.
 * It fetches wallet information and transaction records from API endpoints based on date filters.
 * The component includes expandable monthly transaction lists with date filters using DatePicker.
 */

import React, { useEffect, useState } from 'react';
import "./styleWallete.css";
import { getUserTransactionDetails, getUserWalletDetails } from '../../services/userServices';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { useDispatch, useSelector } from 'react-redux';
import { setTransactions, setWalletDetalData } from '../../features/users/userSlice';

function INCSUBWalleteDetails({ personalDetailData }) {
    const walletlDetailData = useSelector((state)=>state.user.walletlDetailData)
    const transactions = useSelector((state)=>state.user.transactions);
    const navigate = useNavigate();
const dispatch = useDispatch();
    const [startDate, setStartDate] = useState(new Date("2025/04/01"));
    const [endDate, setEndDate] = useState(new Date());
    const [isOpenTxn, setIsOpenTxn] = useState('');

    /**
   * @function fetchWalletsDetailsData
   * @author govind dindewad
   * @description
   * Fetches wallet details for the selected user using their `userId`.
   *
   * - Calls the `getUserWalletDetails` API.
   * - Updates local state (`setWalletDetalData`) with the fetched data.
   * - Handles unauthorized access (401) by redirecting to the login page and clearing local storage.
   * - Displays an error toast for other unexpected responses or API failures.
   *
   * @async
   * @returns {Promise<void>}
   */

    const fetchWalletsDetailsData = async () => {
        try {
            const response = await getUserWalletDetails(personalDetailData?.userId);

            if (response.status === 200) {
                dispatch(setWalletDetalData(response?.data?.data));
            } else if (response.status === 401) {
                // Unauthorized - redirect to login
                navigate('/login');
                window.localStorage.setItem('userData', null);
            } else {
                toast.error(response.data.message);
            }
        } catch (err) {
            toast.error('Something Went Wrong');
        }
    };
    /**
     * @function fetchTransactionDetails
      * @author govind dindewad
     * @description
     * Fetches transaction history for a user filtered by selected start and end dates.
     *
     * - Formats dates to `YYYY-MM-DD` before making the API call.
     * - Calls the `getUserTransactionDetails` API with `userId` and date range.
     * - Updates local state (`setTransactions`) with the transaction data.
     * - Redirects to login if unauthorized.
     * - Displays error messages for failed requests.
     *
     * @async
     * @returns {Promise<void>}
     */

    const fetchTransactionDetails = async () => {
        try {
            const response = await getUserTransactionDetails(
                personalDetailData.userId,
                startDate ? convertDateFormat(startDate) : "",
                endDate ? convertDateFormat(endDate) : ""
            );

            if (response.status === 200) {
                const revList = response?.data?.data.monthlyTransactionDetailDtoList.reverse();
                dispatch(setTransactions({
                    ...response?.data?.data,
                    monthlyTransactionDetailDtoList: revList
                }))

            } else if (response.status === 401) {
                // Unauthorized - redirect to login
                navigate('/login');
                window.localStorage.setItem('userData', null);
            } else {
                toast.error(response.data.message);
            }
        } catch (err) {
            toast.error('Something Went Wrong');
        }
    };

    /**
     * @function convertDateFormat
     * @author govind dindewad
     * @description
     * Converts a JavaScript `Date` object or string into a backend-compatible `YYYY-MM-DD` string format.
     *
     * @param {Date|string} date - The input date to be converted.
     * @returns {string} A date string in 'YYYY-MM-DD' format. Returns an empty string if input is invalid.
     */

    const convertDateFormat = (date) => {
        if (date === '') return "";

        const dateObj = new Date(date);
        const formattedDate = dateObj
            .toLocaleDateString("en-GB", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit"
            })
            .replace("/", "/");

        const splitDate = formattedDate.split('/');

        if (splitDate[2].length === 4) {
            return `${splitDate[2]}-${splitDate[1]}-${splitDate[0]}`;
        }

        return formattedDate;
    };

    /**
  * @function useEffect - Wallet Initialization
  * @author govind dindewad
  * @description
  * Runs once on initial render to load wallet details for the selected user,
  * if `personalDetailData` is available.
  */
    useEffect(() => {
        if (personalDetailData) {
            fetchWalletsDetailsData();
        }
    }, []);

    /**
     * @function useEffect - Transaction Refresh
     * @author govind dindewad
     * @description
     * Triggers when either `startDate` or `endDate` changes.
     * Refetches transaction data based on the new date filters.
     */

    useEffect(() => {
        if (personalDetailData) {
            fetchTransactionDetails();
        }
    }, [startDate, endDate]);

    return (
        <div className='INCSUBwalletedetails'>
            <div className='INCSUBwalletedetails_container'>
                {/* Header */}
                <div className='INCSUBwalletedetails_header_container'>
                    <div className='INCSUBwalletedetails_header'>Wallet Details</div>
                </div>

                {/* Locked & Unlocked Amounts */}
                <div className='INCSUBtotal_amount_user_wallete_container'>
                    <div className='INCSUBtotal_amount_user_container locked'>
                        <div className='INCSUBtotal_amount_user_container_heading'>Locked Amount</div>
                        <div className='INCSUBtotal_amount_user'>₹ {walletlDetailData?.lockedFunds?walletlDetailData?.lockedFunds:"0"}</div>
                    </div>
                    <div className='INCSUBtotal_amount_user_container unlocked'>
                        <div className='INCSUBtotal_amount_user_container_heading'>Unlocked Amount</div>
                        <div className='INCSUBtotal_amount_user'>₹ {walletlDetailData?.unLockedFunds?walletlDetailData?.unLockedFunds:"0"}</div>
                    </div>
                </div>

                {/* Transaction Filter and Summary */}
                <div className='INCSUBuser_transaction_history'>
                    <div className='INCSUBuser_transaction_history_header_container'>
                        <div className='INCSUBuser_transaction_history_date_container'>
                            <div className='INCSUBuser_transaction_history_header'>Transaction history</div>

                            {/* Date Range Picker */}
                            <div className='INCSUBuser_transaction_history_date_picker'>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => {
                                        setStartDate(date);
                                        fetchTransactionDetails();
                                    }}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    maxDate={endDate}
                                    placeholderText='Start Date'
                                    showIcon
                                />
                                &nbsp;{"  -  "}&nbsp;
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => {
                                        setEndDate(date);
                                        fetchTransactionDetails();
                                    }}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    placeholderText='End Date'
                                    showIcon
                                />
                            </div>
                        </div>

                        {/* Total Earned */}
                        <div className='INCSUBuser_total_amount_earned'>
                            Total : ₹ {transactions?.totalAmount}
                        </div>
                    </div>
                </div>

                <hr />

                {/* Monthly Transaction List */}
                <div className='INCSUBuser_txn_user_list'>
                    <div className='INCSUBuser_txn_user_list_container'>
                        {transactions?.monthlyTransactionDetailDtoList?.length > 0 &&
                            transactions.monthlyTransactionDetailDtoList.map((transaction, index) => (
                                <div className='INCSUBuser_txn_user_list_lst' key={index}>
                                    <div
                                        className='INCSUBuser_txn_user_list_lst_details'
                                        onClick={() =>
                                            isOpenTxn !== index ? setIsOpenTxn(index) : setIsOpenTxn('')
                                        }
                                    >
                                        <div className='INCSUBuser_txn_user_list_lst_details_date'>
                                            {isOpenTxn === index ? (
                                                <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
                                                    <path d="M1 7L7 1L13 7" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            ) : (
                                                <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
                                                    <path d="M1 1L7 7L13 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            )}
                                            {transaction?.month} {transaction?.year}
                                        </div>
                                        <div className='INCSUBuser_txn_user_list_total'>
                                            ₹ {transaction?.totalAmount}
                                        </div>
                                    </div>

                                    {/* Expandable transaction items */}
                                    {isOpenTxn === index && (
                                        <div className='INCSUBuser_txn_user_list_lst_details_transactions'>
                                            {transaction.transactionList?.map((detail, txnIndex) => (
                                                <div
                                                    className='INCSUBuser_txn_list'
                                                    key={txnIndex}
                                                    style={{
                                                        borderBottom:
                                                            txnIndex !== transaction.transactionList.length - 1
                                                                ? "0.87px solid #E0E0E0"
                                                                : "none"
                                                    }}
                                                >
                                                    <div className='INCSUBUser_txn_name_detail'>
                                                        <div className='INCSUBUser_txn_name'>{detail.transactionTag}</div>
                                                        <div className='INCSUBUser_txn_date'>{detail.transactionCreatedAt}</div>
                                                    </div>
                                                       <div className='INCSUBUser_txn_money'>{detail?.poolName}</div>
                                                    <div className='INCSUBUser_txn_money'>₹ {detail.transactionAmount}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default INCSUBWalleteDetails;
